import type { TErrorContext, TReporter } from '@cascade/core/errors';
import { toError } from '@cascade/core/utils';
import { initFullStory } from './full-story/index.ts';
import { initGoogleTagManager } from './google-tag-manager/index.ts';
import { initLogRocket } from './log-rocket/index.ts';
import { areIdentitiesEqual } from './observability.helpers.ts';
import type {
  TEventTracker,
  TIdentity,
  TObservabilityOptions,
  TSdk,
} from './observability.types.ts';
import { initSentry } from './sentry/index.ts';

const sdks: TSdk[] = [];
const failedSdks = new Set<TSdk>();

let currentIdentity: TIdentity | undefined;
let isInitialized = false;

export const reportError: TReporter = (error, context) => {
  const reportingSdks = sdks.filter((sdk) => Boolean(sdk.reportError));

  if (reportingSdks.length === 0) {
    console.error(error, context);

    return;
  }

  reportingSdks.forEach((sdk) => {
    try {
      sdk.reportError?.(error, context);
    } catch {
      console.error(error, context);
    }
  });
};

const callSdks = (call: (sdk: TSdk) => void, context: TErrorContext): void => {
  sdks.forEach((sdk) => {
    try {
      call(sdk);
    } catch (thrown) {
      if (failedSdks.has(sdk)) {
        return;
      }

      const failure = toError(thrown);

      failedSdks.add(sdk);
      reportError(failure, context);
    }
  });
};

const identifySdks = (identity: TIdentity): void => {
  callSdks((sdk) => sdk.identify?.(identity), {
    source: 'observability',
    operation: 'identify',
  });
};

const initSdk = (init: () => TSdk): void => {
  try {
    const sdk = init();

    sdks.push(sdk);
  } catch (thrown) {
    const failure = toError(thrown);

    reportError(failure, { source: 'observability', operation: 'init' });
  }
};

export const initObservability = ({
  isEnabled,
  environment,
  release,
  sentryDsn,
  fullStoryOrgId,
  logRocketAppId,
  googleTagManagerId,
}: TObservabilityOptions): void => {
  if (!isEnabled || isInitialized) {
    return;
  }

  isInitialized = true;

  if (sentryDsn) {
    initSdk(() => initSentry({ dsn: sentryDsn, environment, release }));
  }

  if (fullStoryOrgId) {
    initSdk(() => initFullStory({ orgId: fullStoryOrgId }));
  }

  if (logRocketAppId) {
    initSdk(() => initLogRocket({ appId: logRocketAppId, release }));
  }

  if (googleTagManagerId) {
    initSdk(() => initGoogleTagManager({ containerId: googleTagManagerId }));
  }

  if (currentIdentity) {
    identifySdks(currentIdentity);
  }
};

export const setIdentity = (identity?: TIdentity): void => {
  if (areIdentitiesEqual(currentIdentity, identity)) {
    return;
  }

  currentIdentity = identity;

  if (identity) {
    identifySdks(identity);

    return;
  }

  callSdks((sdk) => sdk.anonymize?.(), {
    source: 'observability',
    operation: 'anonymize',
  });
};

export const trackEvent: TEventTracker = (name, properties) => {
  callSdks((sdk) => sdk.track(name, properties), {
    source: 'tracking',
    event: name,
  });
};
